import { InviteLock } from "@/components/growth/InviteLock";
import { useCallback, useEffect, useState } from "react";
import { Coins, Package, Loader2, Bookmark, Lock, Target, Gift, History, Heart } from "lucide-react";
import { Link } from "react-router-dom";
import { GoldCheck } from "@/components/GoldCheck";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatErr, imgSrc } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PointsPausedNotice } from "@/components/PointsPausedNotice";
import { PageExplain } from "@/components/PageExplain";
import { useGoal, GoalCard, RewardDetail, GiftReveal, RedemptionHistory, TypeLabel, fmt } from "@/components/rewards/RewardParts";
import { SheenBar } from "@/components/home/HomeHero";
import { useCountUp } from "@/components/PointsBurst";

const FILTERS = [{ id: "all", label: "Alle" }, { id: "voucher", label: "Gutscheine" }, { id: "physical", label: "Merch & Geschenke" }, { id: "wish", label: "Gemerkt" }];
const SORTS = [["recommended", "Empfohlen"], ["reachable", "Erreichbar zuerst"], ["cheap", "Günstigste"], ["expensive", "Teuerste"]];
const sorter = (mode, points) => (a, b) => (mode === "cheap" ? a.cost - b.cost : mode === "expensive" ? b.cost - a.cost : mode === "reachable" ? (b.cost <= points) - (a.cost <= points) || a.cost - b.cost : 0);
const EMPTY_ADDR = { full_name: "", street: "", zip: "", city: "", country: "Deutschland" };

const AddressForm = ({ addr, setAddr }) => (
  <div className="grid grid-cols-2 gap-3">
    {[["full_name", "Vor- & Nachname", 2], ["street", "Straße & Hausnummer", 2], ["zip", "PLZ", 1], ["city", "Stadt", 1], ["country", "Land", 2]].map(([k, l, span]) => (
      <div key={k} className={`space-y-1.5 ${span === 2 ? "col-span-2" : ""}`}>
        <Label className="text-xs">{l}</Label>
        <Input data-testid={`address-${k}-input`} className="rounded-xl" value={addr[k] || ""} onChange={(e) => setAddr({ ...addr, [k]: e.target.value })} />
      </div>
    ))}
  </div>
);

function RedeemForm({ reward, onSuccess }) {
  const { user } = useAuth();
  const points = user?.points ?? 0;
  const enough = points >= reward.cost;
  const [addr, setAddr] = useState({ ...EMPTY_ADDR, ...(user?.address || {}) });
  const [busy, setBusy] = useState(false);
  const redeem = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/rewards/${reward.id}/redeem`, { address: reward.type === "physical" ? addr : null });
      onSuccess(data);
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  return (
    <>
      <DialogHeader>
        <DialogTitle className="font-display text-2xl">{reward.title}</DialogTitle>
        <DialogDescription>{`Kostet ${fmt(reward.cost)} Punkte. Du hast ${fmt(points)} Punkte – danach bleiben ${fmt(Math.max(0, points - reward.cost))}.`}</DialogDescription>
      </DialogHeader>
      {reward.type === "physical" && <><p className="text-sm font-semibold">Lieferadresse</p><AddressForm addr={addr} setAddr={setAddr} /></>}
      <button onClick={redeem} disabled={busy || !enough} data-testid="redeem-confirm-btn"
        className="tap w-full h-12 rounded-full pink-gradient pink-glow text-white font-bold flex items-center justify-center gap-2 disabled:opacity-50">
        {busy && <Loader2 className="w-4 h-4 animate-spin" />} {enough ? `Für ${fmt(reward.cost)} Punkte einlösen` : "Noch nicht genug Punkte"}
      </button>
    </>
  );
}

function RedeemDialog({ reward, onClose, onDone }) {
  const [result, setResult] = useState(null);
  const onSuccess = (data) => { setResult(data); onDone(); };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-3xl max-w-md" data-testid="redeem-dialog">
        {result ? <GiftReveal result={result} onClose={onClose} /> : <RedeemForm reward={reward} onSuccess={onSuccess} />}
      </DialogContent>
    </Dialog>
  );
}

const REWARD_STEPS = [
  "Tippe auf eine Prämie, um alle Details zu sehen.",
  "Mit der Zielscheibe legst du deine Wunschprämie fest – ihren Fortschritt siehst du hier und auf der Startseite.",
  "Gutscheine: Der Code erscheint sofort nach dem Einlösen (und bleibt unter „Meine Einlösungen“ gespeichert).",
  "Merch & Geschenke: Gib deine Lieferadresse an – wir schicken es dir zu. Den Status siehst du unter „Meine Einlösungen“.",
  "Mit dem Lesezeichen merkst du dir Prämien. Wir schicken dir eine Push-Nachricht, wenn du fast genug Punkte hast (80 %) und sobald du einlösen kannst.",
];

const IconBtn = ({ on, onClick, testId, label, right, onCls, Icon, fill }) => (
  <button onClick={onClick} data-testid={testId} data-on={on || undefined} aria-label={label}
    className={`absolute top-3 ${right} z-10 w-9 h-9 rounded-full flex items-center justify-center backdrop-blur transition-[transform,background-color] active:scale-90 ${on ? onCls : "bg-white/90 text-slate-600 hover:text-rose-600"}`}>
    <Icon className={`w-4 h-4 ${on && fill ? "fill-white" : ""}`} />
  </button>
);

const VipLabel = () => (
  <span className="absolute bottom-3 left-3 vip-gold-bg vip-sheen inline-flex items-center gap-1 px-2.5 h-7 rounded-full border border-rose-300/60 shadow-lg"><Heart className="w-3.5 h-3.5 text-rose-200" fill="currentColor" /><span className="text-[10px] font-extrabold tracking-[0.1em] vip-gold-text">NUR EINLÖSBAR FÜR PROJEKT UNTERSTÜTZER</span></span>
);

function RewardCard({ r, i, points, onSelect, wished, onWish, vip, isGoal, onGoal, onDetail }) {
  const soldOut = r.available === 0;
  const locked = r.vip_only && !vip;
  const pct = Math.min(100, Math.round((points / r.cost) * 100));
  return (
    <article data-testid={`reward-card-${r.id}`} data-vip={r.vip_only || undefined} className={`fade-up rounded-3xl overflow-hidden flex flex-col hover:-translate-y-1 hover:shadow-xl hover:shadow-rose-100 transition-[transform,box-shadow] duration-300 ${r.vip_only ? "vip-card" : "lux-card"} ${isGoal ? "ring-2 ring-amber-300" : ""}`} style={{ animationDelay: `${i * 60}ms` }}>
      <div className="relative aspect-[4/3] bg-rose-50 overflow-hidden group">
        <button type="button" onClick={() => onDetail(r)} data-testid={`reward-open-${r.id}`} aria-label={`${r.title} ansehen`} className="absolute inset-0 z-[1]" />
        {r.image ? <img src={imgSrc(r.image)} alt={r.title} loading="lazy" className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 ${locked ? "saturate-50" : ""}`} /> : <div className="w-full h-full flex items-center justify-center"><Package className="w-12 h-12 text-rose-300" /></div>}
        {locked && <div className="absolute inset-0 bg-[#1d0b12]/55 backdrop-blur-[2px] flex flex-col items-center justify-center gap-2 pointer-events-none" data-testid={`reward-sealed-${r.id}`}>
          <span className="relative w-16 h-16 rounded-full vip-gold-metal flex items-center justify-center shadow-xl ring-4 ring-[#f6e3a6]/30"><Lock className="w-7 h-7" /><span className="absolute -top-1 -right-1"><GoldCheck className="w-6 h-6" /></span></span>
          <span className="px-3 py-1 rounded-full vip-gold-bg border border-[#c99a3c]/60 text-[10px] font-extrabold tracking-[0.15em] uppercase"><span className="vip-gold-text">Versiegelt</span></span>
        </div>}
        {r.vip_only && <VipLabel />}
        <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-white/90 text-xs font-bold flex items-center gap-1 text-slate-700 pointer-events-none"><TypeLabel type={r.type} /></span>
        <IconBtn on={wished} onClick={() => onWish(r)} testId={`reward-wish-btn-${r.id}`} label={wished ? "Nicht mehr merken" : "Merken"} right="right-3" onCls="bg-rose-500 text-white" Icon={Bookmark} fill />
        {!locked && <IconBtn on={isGoal} onClick={() => onGoal(r)} testId={`reward-goal-btn-${r.id}`} label={isGoal ? "Ziel entfernen" : "Als Wunschprämie setzen"} right="right-14" onCls="bg-amber-400 text-amber-950 glow-gold" Icon={Target} />}
      </div>
      <div className="p-5 flex-1 flex flex-col gap-3">
        <button type="button" onClick={() => onDetail(r)} className="flex-1 text-left" data-testid={`reward-title-${r.id}`}><h3 className="font-bold">{r.title}</h3><p className="text-sm text-slate-500 mt-1 line-clamp-2">{r.description}</p></button>
        <SheenBar pct={pct} className="h-2" track="bg-rose-50" fill="bg-gradient-to-r from-pink-500 to-rose-500" testId={`reward-progress-${r.id}`} />
        <div className="flex items-center justify-between gap-2">
          <span className="font-display font-extrabold text-lg text-amber-600 flex items-center gap-1"><Coins className="w-4 h-4" /> {fmt(r.cost)}</span>
          <span className={`text-xs font-bold text-right ${pct >= 100 ? "text-emerald-600" : "text-slate-500"}`} data-testid={`reward-missing-${r.id}`}>{pct >= 100 ? "✓ Einlösbar" : `Noch ${fmt(r.cost - points)}`}{r.available != null && <span className="block font-semibold text-slate-400">{soldOut ? "Ausverkauft" : `Noch ${r.available} verfügbar`}</span>}</span>
        </div>
        {wished && <p className="text-xs font-semibold text-rose-600" data-testid={`reward-wish-status-${r.id}`}>{points >= r.cost ? "Gemerkt – du kannst es jetzt einlösen!" : `Gemerkt – noch ${fmt(r.cost - points)} Punkte`}</p>}
        {locked ? (
          <Link to="/community-unterstuetzen" data-testid={`reward-vip-locked-${r.id}`} className="h-11 rounded-full vip-gold-bg text-amber-300 font-bold text-sm flex items-center justify-center gap-1.5 border border-amber-500/50 hover:brightness-125 transition-[filter]"><Lock className="w-4 h-4" /> Projekt Unterstützer werden</Link>
        ) : (
          <button onClick={() => onSelect(r)} disabled={soldOut} data-testid={`reward-redeem-btn-${r.id}`}
            className={`tap h-11 rounded-full font-bold text-sm transition-[filter,background-color] disabled:bg-slate-200 disabled:text-slate-400 ${r.vip_only ? "bg-gradient-to-r from-pink-300 via-rose-300 to-amber-300 text-[#5b0f2a] hover:brightness-105" : "pink-gradient text-white hover:brightness-105"}`}>
            {soldOut ? "Ausverkauft" : r.vip_only ? "Unterstützer-Geschenk einlösen" : "Einlösen"}
          </button>
        )}
      </div>
    </article>
  );
}

const Pill = ({ on, onClick, testId, children }) => (
  <button onClick={onClick} data-testid={testId} className={`tap px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${on ? "bg-rose-500 text-white tab-glow" : "bg-white border border-pink-100 text-slate-600 hover:bg-rose-50"}`}>{children}</button>
);

const FilterBar = ({ filter, setFilter, cats = [], sort, setSort }) => (
  <div className="flex flex-wrap items-center gap-2">
    {[...FILTERS.slice(0, 1), ...cats.map((c) => ({ id: `cat:${c}`, label: c })), ...FILTERS.slice(1)].map((f) => (
      <Pill key={f.id} on={filter === f.id} onClick={() => setFilter(f.id)} testId={`rewards-filter-${f.id}`}>{f.label}</Pill>
    ))}
    <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="rewards-sort" aria-label="Sortieren" className="ml-auto h-10 px-3 rounded-full bg-white border border-pink-100 text-sm font-semibold text-slate-600">
      {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  </div>
);

function ShopHero({ points, affordable }) {
  const shown = useCountUp(points, 1100, 2000);
  return (
    <div className="lux-hero relative overflow-hidden rounded-[2rem] p-5 sm:p-7 text-white" data-testid="rewards-hero">
      <span className="lux-hero-orb w-52 h-52 -top-20 -right-12" />
      <p className="relative text-[11px] font-extrabold uppercase tracking-[0.3em] text-white drop-shadow">Prämien-Shop</p>
      <h1 className="relative font-display text-3xl sm:text-4xl font-black tracking-tight text-white">Punkte eintauschen</h1>
      <div className="relative mt-4 flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-2 px-4 h-11 rounded-full bg-white text-rose-600 font-display text-xl font-black shadow-lg tabular-nums" data-testid="rewards-balance"><Coins className="w-5 h-5 text-amber-500" /> {fmt(shown)} Punkte</span>
        <span className="inline-flex items-center gap-1.5 px-3 h-9 rounded-full bg-rose-900/35 ring-1 ring-white/40 text-white text-xs font-extrabold" data-testid="rewards-affordable"><Gift className="w-4 h-4" /> {affordable} {affordable === 1 ? "Prämie" : "Prämien"} jetzt einlösbar</span>
      </div>
    </div>
  );
}

export default function Rewards() {
  const { user, refreshUser } = useAuth();
  const [rewards, setRewards] = useState(null);
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("recommended");
  const [tab, setTab] = useState("shop");
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [wish, setWish] = useState([]);
  const { goal, choose } = useGoal();
  const load = useCallback(() => {
    api.get("/rewards").then(({ data }) => setRewards(data));
    api.get("/wishlist").then(({ data }) => setWish(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  const onWish = async (r) => {
    try {
      const { data } = await api.post(`/wishlist/${r.id}`);
      setWish((w) => (data.wished ? [...w, r.id] : w.filter((x) => x !== r.id)));
      toast.success(data.wished ? "Gemerkt – wir melden uns per Push, wenn du es fast geschafft hast ✨" : "Nicht mehr gemerkt");
    } catch (e) { toast.error(formatErr(e)); }
  };
  const vip = Boolean(user?.supporter) && (!user?.supporter_until || new Date(user.supporter_until) > new Date());
  const points = user?.points ?? 0;
  const match = (r) => filter === "all" || (filter === "wish" ? wish.includes(r.id) : filter.startsWith("cat:") ? r.category === filter.slice(4) : r.type === filter);
  const list = (rewards || []).filter((r) => !r.vip_only && !r.premium_only && match(r)).sort(sorter(sort, points));
  const vipList = (rewards || []).filter((r) => r.vip_only && match(r)).sort(sorter(sort, points));
  const cats = [...new Set((rewards || []).map((r) => r.category).filter(Boolean))];
  const affordable = (rewards || []).filter((r) => !r.premium_only && (!r.vip_only || vip) && r.cost <= points && r.available !== 0).length;
  const goalReward = goal && (rewards || []).find((r) => r.id === goal.id);
  const card = (r, i) => <RewardCard key={r.id} r={r} i={i} points={points} onSelect={setSelected} wished={wish.includes(r.id)} onWish={onWish} vip={vip} isGoal={goal?.id === r.id} onGoal={choose} onDetail={setDetail} />;

  return (
    <div className="space-y-6" data-testid="rewards-page">
      <PointsPausedNotice />
      <ShopHero points={points} affordable={affordable} />
      <InviteLock gate="rewards" />
      <GoalCard goal={goal} points={points} onOpen={() => goalReward && setDetail(goalReward)} />
      <div className="flex gap-2 p-1 rounded-full bg-rose-50 w-fit" role="tablist">
        <button role="tab" aria-selected={tab === "shop"} onClick={() => setTab("shop")} data-testid="rewards-tab-shop" className={`tap px-5 h-10 rounded-full text-sm font-bold inline-flex items-center gap-1.5 ${tab === "shop" ? "bg-white text-rose-600 shadow" : "text-slate-500"}`}><Gift className="w-4 h-4" /> Shop</button>
        <button role="tab" aria-selected={tab === "history"} onClick={() => setTab("history")} data-testid="rewards-tab-history" className={`tap px-5 h-10 rounded-full text-sm font-bold inline-flex items-center gap-1.5 ${tab === "history" ? "bg-white text-rose-600 shadow" : "text-slate-500"}`}><History className="w-4 h-4" /> Meine Einlösungen</button>
      </div>
      {tab === "history" ? <RedemptionHistory /> : (<>
        <PageExplain testId="rewards-explain" steps={REWARD_STEPS} />
        <FilterBar filter={filter} setFilter={setFilter} cats={cats} sort={sort} setSort={setSort} />
        {!rewards ? <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-rose-500" /></div> : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="rewards-list">
            {list.length === 0 && <p className="text-slate-400 col-span-full text-center py-10">Hier gibt's gerade nichts – schau bald wieder rein!</p>}
            {list.map(card)}
          </div>
        )}
        {rewards && vipList.length > 0 && (
          <section className="relative overflow-hidden rounded-[2rem] vip-lux p-5 sm:p-8 border border-[#c99a3c]/50 space-y-5" data-testid="rewards-vip-section">
            <span className="vip-lux-pattern" aria-hidden />
            <div className="relative flex items-center gap-3">
              <span className="w-12 h-12 rounded-2xl vip-gold-metal flex items-center justify-center shrink-0"><Lock className="w-6 h-6" /></span>
              <div className="min-w-0">
                <p className="text-[11px] font-extrabold tracking-[0.3em] uppercase text-[#e6c56f]">Exklusiv & versiegelt</p>
                <h2 className="font-display text-2xl font-extrabold vip-gold-title">Geschenke für Projekt Unterstützer</h2>
                <p className="text-sm text-[#cdb88f]">{vip ? "Danke für deine Unterstützung – diese Geschenke kannst du einlösen 💖" : "Nur einlösbar für Projekt Unterstützer – sie tragen die Kosten der Community mit."}</p>
              </div>
            </div>
            <div className="relative grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="rewards-vip-list">{vipList.map(card)}</div>
            {!vip && <Link to="/community-unterstuetzen" data-testid="rewards-vip-become" className="relative inline-flex h-11 px-5 items-center gap-2 rounded-full vip-gold-metal font-extrabold text-sm">Projekt Unterstützer werden</Link>}
          </section>
        )}
      </>)}
      {detail && (
        <Dialog open onOpenChange={(o) => !o && setDetail(null)}>
          <DialogContent className="rounded-3xl max-w-md max-h-[92vh] overflow-y-auto [&>button]:hidden" data-testid="reward-detail-dialog">
            <DialogTitle className="sr-only">{detail.title}</DialogTitle>
            <DialogDescription className="sr-only">Prämiendetails</DialogDescription>
            <RewardDetail r={detail} points={points} wished={wish.includes(detail.id)} isGoal={goal?.id === detail.id} locked={detail.vip_only && !vip}
              onWish={() => onWish(detail)} onGoal={() => choose(detail)} onClose={() => setDetail(null)} onRedeem={() => { setSelected(detail); setDetail(null); }} />
          </DialogContent>
        </Dialog>
      )}
      {selected && <RedeemDialog reward={selected} onClose={() => setSelected(null)} onDone={() => { load(); refreshUser(); }} />}
    </div>
  );
}

export { AddressForm, EMPTY_ADDR };
